import { MemoryProbe, MemorySample, MemoryHistoryRow } from "./memory_probe.js";

type Row = { val: HTMLSpanElement; bar: HTMLDivElement | null };

const VISIBLE_KEY = "bb_mem_dashboard_v1";
const MB = 1048576;

export class MemoryDashboard
{
  private static container: HTMLDivElement | null = null;
  private static rows: Record<string, Row> = {};
  private static chart: HTMLCanvasElement | null = null;
  private static unsubscribe: ( () => void ) | null = null;
  private static peak_estimate = 0;
  private static peak_renderer = 0;
  private static peak_app = 0;

  public static restore(): void
  {
    try
    {
      if ( localStorage.getItem( VISIBLE_KEY ) === "1" )
        this.show();
    }
    catch {}
  }

  public static show(): void
  {
    if ( this.container )
      return;
    this.build();
    this.unsubscribe = MemoryProbe.subscribe( s => this.render( s ) );
    const last = MemoryProbe.last();
    if ( last )
      this.render( last );
    try { localStorage.setItem( VISIBLE_KEY, "1" ); }
    catch {}
  }

  public static hide(): void
  {
    if ( !this.container )
      return;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.container.remove();
    this.container = null;
    this.chart = null;
    this.rows = {};
    try { localStorage.removeItem( VISIBLE_KEY ); }
    catch {}
  }

  public static toggle(): void
  {
    this.container ? this.hide() : this.show();
  }

  public static isVisible(): boolean
  {
    return this.container !== null;
  }

  private static build(): void
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = [
      "position:absolute",
      "top:calc(env(safe-area-inset-top, 0px) + 6px)",
      "left:6px",
      "z-index:2147483645",
      "pointer-events:none",
      "width:250px",
      "background:rgba(0,0,0,0.82)",
      "border:1px solid rgba(255,255,255,0.15)",
      "border-radius:8px",
      "padding:6px 9px 8px",
      "font:11px/1.4 monospace",
      "color:#fff",
      "box-shadow:0 2px 14px rgba(0,0,0,0.6)",
    ].join( ";" );

    this.title( wrap, "◆ RAM DASHBOARD" );

    this.section( wrap, "DEVICE (native)" );
    this.row( wrap, "dev_total", "RAM total", false );
    this.row( wrap, "dev_avail", "RAM free", true );
    this.row( wrap, "app", "App process", true );
    this.row( wrap, "renderer", "WebView renderer", true );
    this.row( wrap, "gpu", "GPU process", false );

    this.section( wrap, "WEBVIEW ESTIMATE (JS side)" );
    this.row( wrap, "est_total", "Total est.", true );
    this.row( wrap, "js_heap", "JS heap", true );
    this.row( wrap, "canvases", "Canvas buffers", true );
    this.row( wrap, "gl_fb", "GL framebuffer", false );
    this.row( wrap, "three_tex", "3D textures", true );
    this.row( wrap, "three_geo", "3D geometry", true );
    this.row( wrap, "three_shadow", "Shadow maps", false );
    this.row( wrap, "images", "2D images", true );
    this.row( wrap, "vfx", "VFX buffers", false );
    this.row( wrap, "audio", "Audio buffers", false );

    this.section( wrap, "PEAKS THIS RUN" );
    this.row( wrap, "peaks", "est / app / rend", false );

    this.section( wrap, "HISTORY 5 MIN (est ▬ renderer ▬ free ▬)" );
    const chart = document.createElement( "canvas" );
    chart.width = 232;
    chart.height = 44;
    chart.style.cssText = "display:block;width:232px;height:44px;background:rgba(255,255,255,0.05);border-radius:4px";
    wrap.appendChild( chart );
    this.chart = chart;

    this.section( wrap, "BEFORE LAST RELOAD" );
    this.row( wrap, "prev", "last / peak", false );

    document.documentElement.appendChild( wrap );
    this.container = wrap;
    this.renderPrevious();
  }

  private static render( s: MemorySample ): void
  {
    if ( !this.container )
      return;
    const e = s.estimate;
    const n = s.native;
    const total = n?.device_total ?? -1;
    const avail = n?.device_available ?? -1;
    const app = n?.app_footprint ?? -1;
    const app_limit = n?.app_limit ?? -1;
    const renderer = n?.renderer_footprint ?? -1;
    const gpu = n?.gpu_footprint ?? -1;

    this.peak_estimate = Math.max( this.peak_estimate, e.total_bytes );
    this.peak_app = Math.max( this.peak_app, app );
    this.peak_renderer = Math.max( this.peak_renderer, renderer );

    this.set( "dev_total", n ? this.fmt( total ) : "no native", "#aaa" );
    this.set( "dev_avail", avail >= 0 ? this.fmt( avail ) : ( n?.platform === "ios" ? "iOS hides" : "N/A" ), avail >= 0 ? this.colorFree( avail, total ) : "#555", avail >= 0 ? this.pct( avail, total ) : 0 );
    this.set( "app", app >= 0 ? `${this.fmt( app )}${app_limit > 0 ? ` / ${this.fmt( app_limit )}` : ""}` : "N/A", this.colorUsed( app, app_limit > 0 ? app_limit : 600 * MB ), this.pct( app, app_limit > 0 ? app_limit : 600 * MB ) );
    this.set( "renderer", renderer >= 0 ? this.fmt( renderer ) : ( n?.platform === "ios" ? "not visible on iOS" : n ? "sandboxed, use adb" : "N/A" ), this.colorUsed( renderer, 1400 * MB ), this.pct( renderer, 1400 * MB ) );
    this.set( "gpu", gpu >= 0 ? this.fmt( gpu ) : "—", "#aaa" );

    this.set( "est_total", this.fmt( e.total_bytes ), this.colorUsed( e.total_bytes, 1200 * MB ), this.pct( e.total_bytes, 1200 * MB ) );
    this.set( "js_heap", e.js_heap >= 0 ? `${this.fmt( e.js_heap )} / ${this.fmt( e.js_heap_limit )}` : "N/A (WebKit)", e.js_heap >= 0 ? this.colorUsed( e.js_heap, e.js_heap_limit ) : "#555", e.js_heap >= 0 ? this.pct( e.js_heap, e.js_heap_limit ) : 0 );
    this.set( "canvases", `${this.fmt( e.canvases_bytes )} (${e.canvases_n})`, this.colorUsed( e.canvases_bytes, 300 * MB ), this.pct( e.canvases_bytes, 300 * MB ) );
    this.set( "gl_fb", this.fmt( e.gl_framebuffer_bytes ), "#aaa" );
    this.set( "three_tex", `${this.fmt( e.three_texture_bytes )} (${e.three_textures_n}/${e.three_info_textures})`, this.colorUsed( e.three_texture_bytes, 400 * MB ), this.pct( e.three_texture_bytes, 400 * MB ) );
    this.set( "three_geo", `${this.fmt( e.three_geometry_bytes )} (${e.three_geometries_n}/${e.three_info_geometries})`, this.colorUsed( e.three_geometry_bytes, 300 * MB ), this.pct( e.three_geometry_bytes, 300 * MB ) );
    this.set( "three_shadow", this.fmt( e.three_shadow_bytes ), "#aaa" );
    this.set( "images", `${this.fmt( e.images_bytes )} (${e.images_n})`, this.colorUsed( e.images_bytes, 300 * MB ), this.pct( e.images_bytes, 300 * MB ) );
    this.set( "vfx", this.fmt( e.instance_buffers_bytes ), "#aaa" );
    this.set( "audio", `${this.fmt( e.audio_bytes )} (${e.audio_n})`, "#aaa" );

    this.set( "peaks", `${this.fmt( this.peak_estimate )} / ${this.fmt( this.peak_app )} / ${this.fmt( this.peak_renderer )}`, "#fa0" );

    this.drawChart( MemoryProbe.history() );
  }

  private static renderPrevious(): void
  {
    const rows = MemoryProbe.previousRun();
    if ( !rows.length )
    {
      this.set( "prev", "no data", "#555" );
      return;
    }
    const last = rows[ rows.length - 1 ];
    const peak = rows.reduce( ( m, r ) => Math.max( m, r[ 1 ] ), 0 );
    const peak_r = rows.reduce( ( m, r ) => Math.max( m, r[ 7 ] ), -1 );
    const age = Math.round( ( Date.now() - last[ 0 ] ) / 1000 );
    const renderer_part = peak_r >= 0 ? `, rend ${last[ 7 ]}/${peak_r} MB` : "";
    this.set( "prev", `est ${last[ 1 ]}/${peak} MB${renderer_part}, ${age}s ago`, "#f88" );
  }

  private static drawChart( rows: MemoryHistoryRow[] ): void
  {
    const c = this.chart;
    if ( !c )
      return;
    const ctx = c.getContext( "2d" );
    if ( !ctx )
      return;
    const w = c.width;
    const h = c.height;
    ctx.clearRect( 0, 0, w, h );
    if ( rows.length < 2 )
      return;
    let max = 1;
    for ( const r of rows )
      max = Math.max( max, r[ 1 ], r[ 7 ], r[ 8 ] );
    const series: { idx: number; color: string }[] = [
      { idx: 1, color: "#5d5" },
      { idx: 7, color: "#f55" },
      { idx: 8, color: "#59f" },
    ];
    for ( const s of series )
    {
      ctx.beginPath();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1;
      let started = false;
      rows.forEach( ( r, i ) =>
      {
        const v = r[ s.idx ];
        if ( v < 0 )
          return;
        const x = ( i / ( rows.length - 1 ) ) * ( w - 2 ) + 1;
        const y = h - 1 - ( v / max ) * ( h - 2 );
        if ( !started ) { ctx.moveTo( x, y ); started = true; }
        else ctx.lineTo( x, y );
      } );
      ctx.stroke();
    }
    ctx.fillStyle = "#888";
    ctx.font = "9px monospace";
    ctx.fillText( `${max} MB`, 2, 9 );
  }

  private static fmt( bytes: number ): string
  {
    return bytes < 0 ? "N/A" : `${( bytes / MB ).toFixed( bytes >= 100 * MB ? 0 : 1 )} MB`;
  }

  private static pct( v: number, max: number ): number
  {
    return v >= 0 && max > 0 ? Math.min( 100, ( v / max ) * 100 ) : 0;
  }

  private static colorUsed( v: number, max: number ): string
  {
    if ( v < 0 )
      return "#555";
    return v > max * 0.8 ? "#f55" : v > max * 0.5 ? "#fa0" : "#5d5";
  }

  private static colorFree( free: number, total: number ): string
  {
    if ( free < 0 || total <= 0 )
      return "#555";
    const ratio = free / total;
    return ratio < 0.1 ? "#f55" : ratio < 0.25 ? "#fa0" : "#5d5";
  }

  private static title( parent: HTMLElement, text: string ): void
  {
    const el = document.createElement( "div" );
    el.textContent = text;
    el.style.cssText = "color:#888;font-size:10px;letter-spacing:1px;margin-bottom:4px";
    parent.appendChild( el );
  }

  private static section( parent: HTMLElement, text: string ): void
  {
    const el = document.createElement( "div" );
    el.textContent = text;
    el.style.cssText = "color:#555;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;margin:6px 0 3px";
    parent.appendChild( el );
  }

  private static row( parent: HTMLElement, key: string, label: string, has_bar: boolean ): void
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = "margin-bottom:2px";
    const lbl = document.createElement( "span" );
    lbl.textContent = label;
    lbl.style.cssText = "color:#777;display:inline-block;width:108px;font-size:10px";
    const val = document.createElement( "span" );
    val.style.cssText = "font-size:11px;font-weight:bold";
    wrap.appendChild( lbl );
    wrap.appendChild( val );
    let bar: HTMLDivElement | null = null;
    if ( has_bar )
    {
      const track = document.createElement( "div" );
      track.style.cssText = "height:2px;background:rgba(255,255,255,0.07);border-radius:2px;margin-top:1px";
      bar = document.createElement( "div" );
      bar.style.cssText = "height:2px;border-radius:2px;width:100%;transform:scaleX(0);transform-origin:left;transition:transform .5s,background-color .5s";
      track.appendChild( bar );
      wrap.appendChild( track );
    }
    parent.appendChild( wrap );
    this.rows[ key ] = { val, bar };
  }

  private static set( key: string, text: string, color: string, bar_pct?: number ): void
  {
    const ref = this.rows[ key ];
    if ( !ref )
      return;
    ref.val.textContent = text;
    ref.val.style.color = color;
    if ( ref.bar && bar_pct !== undefined )
    {
      ref.bar.style.transform = `scaleX(${( bar_pct / 100 ).toFixed( 3 )})`;
      ref.bar.style.backgroundColor = color;
    }
  }
}
